import React, { useEffect, useState, useContext } from 'react';
import {
  AddMembersConfiguration,
  AvatarStyle,
  CometChatConversations,
  CometChatMessages,
  CometChatThemeContext,
  CometChatUIEvents,
  CometChatUIKit,
  CometChatUIKitConstants,
  ConversationsStyle,
  DetailsConfiguration,
  MessageBubbleAlignment,
  MessageComposerConfiguration,
  MessageComposerStyle,
  MessageHeaderConfiguration,
  MessageListConfiguration,
  MessagesStyle,
} from '@cometchat/chat-uikit-react';
import {
  Badge,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Tab,
  TextField,
  Typography,
  DialogTitle,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  DialogActions,
  Tooltip,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ConversationListView from './ConversationListView';
import TabPanel from './TabPanel';
import {
  setChatConversationStatus,
  getChartId,
  getUnassignedConversations,
  getVMANames,
  assignConversationToOthers,
  assignConversationToSelf,
} from 'store/slices/secureMessageSlice';
import { useAppDispatch } from 'store/hooks';
import { createComponent } from '@lit-labs/react';
import { CometChatTextBubble } from '@cometchat/uikit-elements';
import CreateTask from './Task/CreateTask';
import TaskList from './Task/TaskList';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { toast } from 'react-toastify';
import { AuthContext } from 'utils/AuthContext';
import { roles_key } from 'utils/dropDownList';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { StyledDialog } from 'theme/index';
import { ROUTE } from 'route/RouteEnums';
import { DetailsUtils } from '@cometchat/uikit-shared';

// ✅ Message status ticks
const MessageStatusTicks = ({ message }: { message: CometChat.BaseMessage }) => {
  const delivered = message.getDeliveredAt?.();
  const read = message.getReadAt?.();

  let ticks = '✓';
  let color = '#9e9e9e';

  if (delivered) {
    ticks = '✓✓';
  }

  if (read) {
    ticks = '✓✓';
    color = '#2196F3'; // blue tick
  }

  return (
    <span
      style={{
        fontSize: '12px',
        marginLeft: '6px',
        color,
        fontWeight: 600,
      }}
    >
      {ticks}
    </span>
  );
};

type DetailsTemplate = ReturnType<typeof DetailsUtils.getDefaultDetailsTemplate>[number];

const HIDDEN_DETAILS_OPTIONS = ['Delete and Exit', 'Banned Members'];

const isVisibleDetailsOption = (option: { title?: string }) =>
  !HIDDEN_DETAILS_OPTIONS.includes(option.title ?? '');

const hideRestrictedDetailsOptions = (template: DetailsTemplate) => {
  const originalOptions = template.options;

  if (!originalOptions) {
    return template;
  }

  template.options = (loggedInUser, currentGroup, section, currentUser) =>
    (originalOptions(loggedInUser, currentGroup, section, currentUser) ?? []).filter(
      isVisibleDetailsOption
    );

  return template;
};

const ChatboxAdmin = (props: any) => {
  const [chatWithGroup, setChatWithGroup] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [guardianName, setGuardianName] = useState<any>('');
  const [patientId, setPatientId] = useState<any>();
  const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
  const [showTaskList, setShowTaskList] = useState<boolean>(false);
  const [chartId, setChartId] = useState<any>(null);
  const [groupId, setGroupId] = useState<string>();
  const [editData, setEditData] = useState<any>(null);
  const [patientAvailable, setPatientAvailable] = useState(false);
  const [shortcuts, setShortcuts] = useState<any>();
  const [unassignedConversations, setUnassignedConversations] = useState<any>(null);
  const [showAssignToModal, setShowAssignToModal] = useState<boolean>(false);
  const [vamNames, setVMANames] = useState<any>();
  const [selectedVMA, setSelectedVMA] = useState();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [msgTxt, setMsgTxt] = useState<string>('');
  const [selectedPatientName, setSelectedPatientName] = useState<any>('');
  const [userId, setUserId] = useState<any>('');
  const [removeConfirm, setRemoveConfirm] = useState<{ uid: string; name: string } | null>(null);

  const dispatch = useAppDispatch();
  const conversationStatus = true;
  const user = useContext(AuthContext);
  const { theme } = useContext(CometChatThemeContext);

  const [openManageModal, setOpenManageModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<CometChat.User[]>([]);
  const [currentMembers, setCurrentMembers] = useState<CometChat.GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [messageInfoOpen, setMessageInfoOpen] = useState(false);
  const [receiptTable, setReceiptTable] = useState<any[]>([]);
  const [infoMessage, setInfoMessage] = useState<CometChat.BaseMessage | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>(null);

  // Keep Message Info live: refetch from the server when another user's receipt arrives.
  // Any receipt can matter (markAsRead also marks all earlier messages read), and the
  // realtime event's receiver isn't reliably the group guid, so we don't filter on it.
  // The refetch is delayed slightly so the server has persisted the receipt first.
  useEffect(() => {
    if (!messageInfoOpen || !infoMessage) return;

    const listenerID = 'chatbox-admin-message-info';
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = (receipt: CometChat.MessageReceipt) => {
      if (receipt.getSender()?.getUid() === loggedInUser?.getUid()) return;

      clearTimeout(timer);
      timer = setTimeout(() => refreshReceiptTable(infoMessage), 1000);
    };

    CometChat.addMessageListener(
      listenerID,
      new CometChat.MessageListener({
        onMessagesDelivered: refresh,
        onMessagesRead: refresh,
      })
    );

    return () => {
      clearTimeout(timer);
      CometChat.removeMessageListener(listenerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageInfoOpen, infoMessage]);

  useEffect(() => {
    CometChat.callExtension('message-shortcuts', 'GET', 'v1/fetch', '').then((shortcuts) => {
      setShortcuts(shortcuts);
    });
    getUnassignedMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientAvailable]);

  useEffect(() => {
    if (chatWithGroup) {
      fetchGroupMembers();
    }
  }, [chatWithGroup]);

  useEffect(() => {
    CometChatUIKit.getLoggedinUser().then((user) => {
      setLoggedInUser(user);
    });
  }, []);

  const createDetailsTemplates = (
    user?: CometChat.User,
    group?: CometChat.Group
  ) => {
    const templates = DetailsUtils.getDefaultDetailsTemplate(
      null,
      user ?? null,
      group ?? null
    );

    return templates.map(hideRestrictedDetailsOptions);
  };

  const fetchGroupMembers = async () => {
    if (!chatWithGroup) return;
    setLoadingMembers(true);
    try {
      const membersRequest = new CometChat.GroupMembersRequestBuilder(chatWithGroup.getGuid())
        .setLimit(100)
        .build();
      const members = await membersRequest.fetchNext();
      setCurrentMembers(members);
    } catch (err) {
      console.log('Failed to fetch members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const buildReceiptTable = (
    receipts: CometChat.MessageReceipt[],
    members: CometChat.GroupMember[],
    message: CometChat.BaseMessage
  ) => {
    const receiptMap = new Map(receipts.map((r) => [r.getSender()?.getUid(), r]));
    const senderUid = message.getSender()?.getUid();
    const sentAt = message.getSentAt();

    return (
      members
        // Exclude the sender, and members who joined after the message was sent
        // (unless they still have a receipt for it)
        .filter(
          (member) =>
            member.getUid() !== senderUid &&
            (receiptMap.has(member.getUid()) || !sentAt || member.getJoinedAt() <= sentAt)
        )
        .map((member) => {
          const receipt = receiptMap.get(member.getUid());

          return {
            uid: member.getUid(),
            name: member.getName(),
            avatar: member.getAvatar(),
            deliveredAt: receipt?.getDeliveredAt() ?? null,
            readAt: receipt?.getReadAt() ?? null,
          };
        })
    );
  };

  const loadReceiptTable = async (message: CometChat.BaseMessage) => {
    // Fetch members fresh for the message's group: this is called from inside the
    // message template, so component state (currentMembers) can be stale here.
    const guid = message.getReceiverId();
    const [receipts, members] = await Promise.all([
      CometChat.getMessageReceipts(message.getId()),
      new CometChat.GroupMembersRequestBuilder(guid).setLimit(100).build().fetchNext(),
    ]);

    setReceiptTable(buildReceiptTable(receipts as CometChat.MessageReceipt[], members, message));
  };

  /**
   * Reloads the Message Info receipt table, logging (not throwing) a failed refresh.
   * Kept at component level so the live-receipt listener's timer does not nest functions too deeply.
   */
  const refreshReceiptTable = (message: CometChat.BaseMessage) => {
    loadReceiptTable(message).catch((error) => console.error('Receipt refresh failed', error));
  };

  const handleMessageInfo = async (message: CometChat.BaseMessage) => {
    try {
      await loadReceiptTable(message);
      setInfoMessage(message);
      setMessageInfoOpen(true);
    } catch (error) {
      console.error('Receipt fetch failed', error);
      toast.error('Unable to load message info');
    }
  };

  // 3. Search function
  const searchUsers = async (keyword: string) => {
    if (!keyword.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const builder = new CometChat.UsersRequestBuilder()
        .setLimit(30)
        .setSearchKeyword(keyword)
        .setRoles(roles_key);
      const users = await builder.build().fetchNext();
      const existingUids = currentMembers.map((m) => m.getUid());
      const filtered = users.filter((u) => !existingUids.includes(u.getUid()));
      setSearchResults(filtered);
    } catch (err) {
      console.log('search error');
    }
  };

  const getUnassignedMessages = () => {
    dispatch(getUnassignedConversations({}))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setUnassignedConversations(response?.data);
        }
      });
  };

  const handleConversation = () => {
    setShowConfirmationDialog(true);
  };

  const handleCloseConversation = () => {
    setSelectedTab(3);
    const data = {
      groupId: groupId,
      indicator: 'end',
    };
    dispatch(setChatConversationStatus(data))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          setChatWithGroup(null);
          setSelectedTab(0);
          setShowConfirmationDialog(false);
        }
      });
  };

  const handleAssignTask = () => {
    setEditData(null);
    setShowTaskModal(!showTaskModal);
  };

  const handleEditTask = (data: any) => {
    setShowTaskModal(true);
    setEditData(data);
  };

  function getMentionsTextFormatter(textMessage: CometChat.TextMessage) {
    const mentionsFormatter = CometChatUIKit.getDataSource().getMentionsTextFormatter({ theme });
    mentionsFormatter.setCometChatUserGroupMembers(textMessage.getMentionedUsers());
    mentionsFormatter.setMessage(textMessage);

    mentionsFormatter.setMessageBubbleAlignment(MessageBubbleAlignment.left);

    return mentionsFormatter;
  }

  function getUrlTextFormatter(textMessage: CometChat.TextMessage) {
    const urlFormatter = CometChatUIKit.getDataSource().getUrlTextFormatter({
      theme,
      alignment: MessageBubbleAlignment.left,
    });
    return urlFormatter;
  }

  const TextMessageBubble = createComponent({
    tagName: 'cometchat-text-bubble',
    elementClass: CometChatTextBubble,
    react: React,
  });

  const auxButton = (item: CometChat.User | CometChat.Group, composerId: any) => {
    return (
      <>
        <Button
          variant='text'
          onClick={() => handleConversation()}
          sx={{ opacity: 0.7, fontSize: '14px' }}
        >
          {conversationStatus ? 'CLOSE CONVERSATION' : 'START CONVERSATION'}
        </Button>
        <Button
          variant='text'
          onClick={() => handleAssignTask()}
          sx={{ marginRight: '20px', opacity: 0.7, fontSize: '14px' }}
        >
          ASSIGN TASK
        </Button>
      </>
    );
  };

  const handlePatientClick = (
    item: any,
    patientId?: string,
    name?: string,
    guardianName?: string,
    userId?: string
  ) => {
    setGuardianName(guardianName); // this is user/guardian name
    setSelectedPatientName(name); // this is actual patient name
    setChatWithGroup(item.conversationWith);
    setGroupId(item?.conversationWith?.guid);
    setPatientId(patientId);
    setUserId(userId);
    dispatch(getChartId(patientId))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          setChartId(res?.data?.chartId);
        }
      });
  };

  const getInternalGroups = (item: any) => {
    const groupName = item.conversationId.split('_');
    return (
      <>
        {groupName[1] === 'internal' ? (
          <Grid container>
            <Grid
              item
              sx={{
                borderRadius: '50%',
                background: '#FFECC0',
                color: '#000',
                padding: '10px',
                fontSize: '14px',
                fontWeight: 700,
                height: '36px',
                width: '36px',
                cursor: 'pointer',
              }}
            >
              {groupName[1]?.charAt(0)?.toUpperCase()}
              {groupName[2]?.charAt(0)?.toUpperCase()}
            </Grid>
            <Grid item sx={{ padding: '10px', textTransform: 'capitalize' }}>
              <Badge
                badgeContent={item?.unreadMessageCount}
                sx={{
                  '& .MuiBadge-badge': {
                    color: '#fff',
                    backgroundColor: '#FFC44D!important',
                  },
                }}
              >
                <Typography
                  sx={{ fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => handlePatientClick(item)}
                >
                  {groupName[2]}
                </Typography>
              </Badge>
            </Grid>
          </Grid>
        ) : (
          ''
        )}
      </>
    );
  };

  const getPatientListView = (item: any) => {
    if (
      item?.conversationWith.name !== 'MindWeal Internal' &&
      item?.conversationWith.name !== 'VMAs Only Internal'
    ) {
      const patientName = item.conversationId.split('_');
      if (patientName[1] !== 'internal') {
        setPatientAvailable(true);
      }

      return (
        <ConversationListView
          conversationId={item?.conversationWith?.guid}
          patientName={patientName[1]} // this is used to check whether it is internal group or not
          unreadMessageCount={item?.unreadMessageCount}
          handlePatientClick={(id: any, name: string, guardianName: string, userId: string) =>
            handlePatientClick(item, id, name, guardianName, userId)
          }
        />
      );
    } else return <></>;
  };

  const handleShowAssignToModal = (groupId: any) => {
    setShowAssignToModal(true);
    setGroupId(groupId);
    dispatch(getVMANames({}))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          setVMANames(res?.data);
        }
      });
  };

  const UnassignedConversations = () => {
    return (
      <>
        {unassignedConversations.map((u: any) => {
          return (
            <Grid container key={u.groupId} sx={{ borderBottom: '1px solid #EAECF0' }}>
              <Grid
                item
                xs={1.8}
                sx={{
                  borderRadius: '50%',
                  background: '#FFECC0',
                  color: '#000',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: 700,
                  height: '36px',
                  width: '36px',
                  cursor: 'pointer',
                }}
              >
                {u?.guardianName?.charAt(0)?.toUpperCase()}
                {u?.guardianName?.split(' ')[1]?.charAt(0)?.toUpperCase()}
              </Grid>
              <Grid
                item
                xs={10.2}
                sx={{ padding: '10px 0px 10px 10px', textTransform: 'capitalize' }}
              >
                <Grid container>
                  <Grid item md={user?.user?.permissions.includes('CONVERSATIONS_INSIGHT') ? 9 : 6}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                      {u?.guardianName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 400, cursor: 'pointer' }}>
                      {u?.type}
                    </Typography>
                  </Grid>
                  <Grid item md={user?.user?.permissions.includes('CONVERSATIONS_INSIGHT') ? 3 : 6}>
                    {user?.user?.permissions.includes('CONVERSATIONS_INSIGHT') ? (
                      <Button variant='text' onClick={() => handleShowAssignToModal(u.groupId)}>
                        Assign
                      </Button>
                    ) : (
                      <Button
                        variant='text'
                        onClick={() => handleAssignConversationToSelf(u.groupId)}
                        sx={{ padding: '9px 0px' }}
                      >
                        Assign to Me
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  };
  const composerStyle = new MessageComposerStyle({
    inputBorder: 'none',
    textColor: '#000',
    placeHolderTextColor: '#fff',
    dividerTint: '#EAECF0',
    textFont: 'Graphik-Regular,sans-serif',
  });

  const handleComposerTextChange = (e: any) => {
    setMsgTxt(e);
    if (e && e.charAt(0) === '!') {
      // eslint-disable-next-line array-callback-return
      Object.keys(shortcuts.shortcuts).filter((key) => {
        if (key === e) {
          CometChatUIEvents.ccComposeMessage.next(shortcuts.shortcuts[key]);
        }
      });
    }
  };

  const messageComposerConfig = new MessageComposerConfiguration({
    messageComposerStyle: composerStyle,
    hideVoiceRecording: true,
    hideLiveReaction: true,
    hideLayoutMode: true,
    AIIconURL: 'none',
    emojiIconURL: 'none',
    headerView: () => {
      <></>;
    },
    auxilaryButtonView: (item: CometChat.User | CometChat.Group, composerId: any) => {
      return selectedTab === 0 ? auxButton(item, composerId) : null;
    },
    onTextChange: (e: any) => handleComposerTextChange(e),
    sendButtonView: (...args: any) => {
      return (
        <button
          onClick={() => {
            CometChatUIEvents.ccComposeMessage.next('');
            let receiverID = '';
            const messageText = msgTxt;
            let receiverType: string = CometChat.RECEIVER_TYPE.USER;
            if (args[1].group) {
              receiverType = CometChat.RECEIVER_TYPE.GROUP;
              receiverID = args[1].group;
            } else {
              receiverType = CometChat.RECEIVER_TYPE.USER;
              receiverID = args[1].user;
            }

            const textMessage: CometChat.TextMessage = new CometChat.TextMessage(
              receiverID,
              messageText,
              receiverType
            );
            CometChatUIKit.sendTextMessage(textMessage).then(
              (message: any) => {
                setMsgTxt('');
              },
              (error: CometChat.CometChatException) => {
                console.log('Message sending failed with error:', error);
              }
            );
          }}
        >
          <CardMedia
            component={'img'}
            src='../../images/send.svg'
            alt='card-img1'
            sx={{
              height: '20px',
              width: '20px',
              mb: '3px',
              border: 'none!important',
              background: 'none!important',
              cursor: 'pointer',
            }}
          />
        </button>
      );
    },
  });

  const messageStyle = new MessagesStyle({
    background: '#fff',
  });

  const getConversationStyle = new ConversationsStyle({
    border: 'none',
    background: '#fff',
  });

  /**
   * Renders a text message bubble with its delivery ticks and info button.
   * Kept at component level so the message template override does not nest functions too deeply.
   */
  const renderTextMessageContent = (message: CometChat.BaseMessage) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <TextMessageBubble
          text={(message as CometChat.TextMessage).getText()}
          textStyle={{
            textFont: 'inter',
            textColor: 'black',
          }}
          textFormatters={[
            getUrlTextFormatter(message as CometChat.TextMessage),
            getMentionsTextFormatter(message as CometChat.TextMessage),
          ]}
        />

        {/* ✅ Tick + timestamp row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '6px',
            marginTop: '2px',
          }}
        >
          {message.getSender().getUid() === loggedInUser?.getUid() && (
            <>
              <MessageStatusTicks message={message} />

              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessageInfo(message);
                }}
                sx={{ padding: '2px' }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#3399ff' }} />
              </IconButton>
            </>
          )}
        </div>
      </div>
    );
  };

  const getTemplates = () => {
    const templates = CometChatUIKit?.getDataSource()?.getAllMessageTemplates();
    templates?.forEach((template) => {
      // eslint-disable-next-line max-len
      if (
        template.contentView &&
        template.category === CometChatUIKitConstants.MessageCategory.message &&
        template.type === CometChatUIKitConstants.MessageTypes.text
      ) {
        // eslint-disable-next-line max-len
        const contentView = (template.contentView = (
          message: CometChat.BaseMessage,
          _alignment: MessageBubbleAlignment
        ) => renderTextMessageContent(message));

        template.contentView = (
          message: CometChat.BaseMessage,
          _alignment: MessageBubbleAlignment
        ) => {
          if (message?.getSender()?.getRole() === 'guardian') {
            return (
              <div
                style={{
                  background: '#FFC44D',
                  padding: '3px',
                  borderRadius: '0px 12px 12px 12px',
                  alignItems: 'center',
                  color: '#000!important',
                }}
              >
                {contentView(message, _alignment)}
              </div>
            );
          } else {
            return (
              <div
                style={{
                  background: '#FEF0C7',
                  padding: '3px',
                  borderRadius: '12px 0px 12px 12px',
                  alignItems: 'center',
                  color: '#000!important',
                }}
              >
                {contentView(message, _alignment)}
              </div>
            );
          }
        };
      }
    });
    return templates;
  };

  const messageListConfig = new MessageListConfiguration({
    templates: getTemplates(),
  });

  // eslint-disable-next-line max-len
  const addMemberConfig = new AddMembersConfiguration({
    usersRequestBuilder: new CometChat.UsersRequestBuilder().setLimit(30).setRoles(roles_key),
  });

  const detailsConfig = new DetailsConfiguration({
    addMembersConfiguration: addMemberConfig,
    data: (user, group) => createDetailsTemplates(user, group),
  });

  const headerAvatarStyle = new AvatarStyle({
    backgroundColor: '#FEF0C7',
    nameTextColor: '#000',
  });

  const headerConfig = new MessageHeaderConfiguration({
    avatarStyle: headerAvatarStyle,
    listItemView: () => {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box>
              <Typography variant='h3' sx={{ textTransform: 'capitalize' }}>
                {guardianName} <br />
                {selectedTab === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    
                    <Typography variant='body1'>
                      {selectedPatientName !== ' '
                        ? `${selectedPatientName} (Chart Id: ${chartId})`
                        : 'N/A'}
                      &nbsp;
                      {selectedPatientName !== ' ' && (
                      <Tooltip title='Patient Chart'>
                        <IconButton
                          size='small'
                          aria-label='Patient Chart'
                          onClick={(event) => {
                            event.stopPropagation();
                            openPatientChart(patientId);
                          }}
                          sx={{ bottom: '4px' }}
                        >
                          <PersonIcon fontSize='small' sx={{ color: '#3366FF' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    </Typography>
                    
                  </Box>
                ) : (
                  ''
                )}
              </Typography>
            </Box>
            {selectedTab === 0 ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant='text' onClick={() => setShowTaskList(true)}>
                  View Task
                </Button>
                <Button variant='text' onClick={() => setOpenManageModal(true)}>
                  Manage Members
                </Button>
              </Box>
            ) : null}
          </Box>
        </>
      );
    },
  });

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
    setChatWithGroup(null);
  };

  const handleAssignConversation = () => {
    const data = {
      groupId,
      id: selectedVMA,
    };
    dispatch(assignConversationToOthers(data))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          toast('Conversation Assigned', {
            type: 'success',
            icon: true,
          });
          getUnassignedMessages();
          setShowAssignToModal(false);
        }
      });
  };

  const handleAssignConversationToSelf = (id: any) => {
    dispatch(assignConversationToSelf(id))
      .unwrap()
      .then((response: any) => {
        getUnassignedMessages();
      });
  };

  const handleVMAChange = (e: any) => {
    setSelectedVMA(e.target.value);
  };

  const openPatientChart = (patientId: string | number) => {
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          md={3}
          sx={{
            minHeight: '550px',
            boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
            background: '#fff',
          }}
        >
          <>
            {selectedTab === 3 ? (
              <Grid
                container
                spacing={0}
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={{ minHeight: '50vh' }}
              >
                <Grid item xs={3}>
                  <CircularProgress />
                </Grid>
              </Grid>
            ) : (
              <>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  sx={{
                    background: '#F9FAFB',
                    borderRadius: '23px',
                    marginTop: '10px',
                    '.MuiTab-root.Mui-selected': {
                      background: '#9F84FF',
                      color: '#fff',
                      borderRadius: '23px',
                      fontWeight: 500,
                    },
                  }}
                  TabIndicatorProps={{
                    style: {
                      background: 'transparent',
                    },
                  }}
                  centered
                >
                  <Tab label={'Guardian'} />
                  <Tab label={'Unassigned'} />
                  <Tab label={'Internal'} />
                </Tabs>
                <TabPanel value={selectedTab} index={0}>
                  <CometChatConversations
                    listItemView={getPatientListView}
                    conversationsStyle={getConversationStyle}
                  />
                </TabPanel>
                <TabPanel value={selectedTab} index={1}>
                  <UnassignedConversations />
                </TabPanel>
                <TabPanel value={selectedTab} index={2}>
                  <CometChatConversations
                    listItemView={getInternalGroups}
                    conversationsStyle={getConversationStyle}
                  />
                </TabPanel>
              </>
            )}
          </>
        </Grid>
        <Grid item md={9} sx={{ display: 'flex', height: 'calc(100vh - 65px)' }}>
          {chatWithGroup !== null ? (
            <CometChatMessages
              group={chatWithGroup}
              messagesStyle={messageStyle}
              messageHeaderConfiguration={headerConfig}
              messageComposerConfiguration={messageComposerConfig}
              messageListConfiguration={messageListConfig}
              detailsConfiguration={detailsConfig}
            />
          ) : (
            <>
              <Box
                sx={{
                  alignContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'center',
                  margin: '0 auto',
                }}
              >
                <Typography variant='h2' color={'#000'}>
                  {patientAvailable ? 'Select group to start chat' : 'No chats available'}
                </Typography>
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      {showTaskModal && (
        <CreateTask
          onClose={handleAssignTask}
          editData={editData !== null ? editData : null}
          userId={userId}
          patientId={patientId}
          patientName={editData === null ? selectedPatientName : null}
        />
      )}
      {showTaskList && (
        <TaskList
          patientId={patientId}
          onEdit={handleEditTask}
          onClose={() => setShowTaskList(false)}
        />
      )}

      <StyledDialog open={showAssignToModal} onClose={() => setShowAssignToModal(false)}>
        <IconButton
          aria-label='close'
          onClick={() => setShowAssignToModal(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant='h2'
                sx={{ borderBottom: '1px solid #EAECF0' }}
                textAlign={'center'}
                pb={2}
              >
                Assign Conversation
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='assignedTo'>Assign To*</InputLabel>
              <TextField
                id='assignTo'
                variant='outlined'
                fullWidth
                select
                label='Select VMA'
                onChange={(e: any) => handleVMAChange(e)}
              >
                {vamNames?.length &&
                  vamNames.map((v: any) => {
                    return (
                      <MenuItem key={v.firstName + v.lastName} value={v.userId}>
                        {v.firstName + ' ' + v.lastName}
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
            <Box sx={{ justifyContent: 'center', justifyItems: 'center', m: '45px auto 5px' }}>
              <Button variant='contained' onClick={() => handleAssignConversation()}>
                Assign
              </Button>
            </Box>
          </Grid>
        </DialogContent>
      </StyledDialog>

      <StyledDialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant='h3' textAlign={'center'} pb={2}>
                Close Conversation?
              </Typography>
            </Grid>
            <Grid xs={12} textAlign={'center'} p='30px 40px'>
              <Typography sx={{ fontSize: '14px' }}>
                Are you sure you want to close this conversation?
              </Typography>
            </Grid>
            <Box sx={{ justifyContent: 'center', justifyItems: 'center', m: '25px auto 5px' }}>
              <Button
                variant='contained'
                size='small'
                sx={{
                  marginRight: '8px',
                  background: '#F04438',
                  '&:hover': {
                    background: '#f02719',
                  },
                }}
                onClick={() => handleCloseConversation()}
              >
                Yes
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={() => setShowConfirmationDialog(false)}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </DialogContent>
      </StyledDialog>
      {openManageModal && (
        <StyledDialog open={true} onClose={() => setOpenManageModal(false)} maxWidth='sm' fullWidth>
          <DialogTitle>
            Manage Members
            <IconButton
              onClick={() => setOpenManageModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ maxHeight: '70vh' }}>
            {/* Existing Members */}
            <Typography variant='subtitle1' gutterBottom fontWeight='bold'>
              Current Members ({currentMembers.length})
            </Typography>
            <Box sx={{ maxHeight: 250, overflow: 'auto', mb: 3 }}>
              {loadingMembers ? (
                <CircularProgress size={24} />
              ) : currentMembers.length === 0 ? (
                <Typography color='text.secondary'>No members yet</Typography>
              ) : (
                currentMembers.map((member) => (
                  <ListItem
                    key={member.getUid()}
                    secondaryAction={
                      <IconButton
                        edge='end'
                        onClick={() => {
                          setRemoveConfirm({
                            uid: member.getUid(),
                            name: member.getName() || member.getUid(),
                          });
                        }}
                      >
                        <DeleteIcon color='error' />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={member.getAvatar()} />
                    </ListItemAvatar>
                    <ListItemText primary={member.getName()} secondary={member.getScope()} />
                  </ListItem>
                ))
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            {/* Add New Member */}
            <Typography variant='subtitle1' gutterBottom fontWeight='bold'>
              Add New Member
            </Typography>
            <TextField
              fullWidth
              placeholder='Search users...'
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                searchUsers(e.target.value);
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {searchResults.map((user) => (
                <ListItem
                  button
                  key={user.getUid()}
                  onClick={async () => {
                    if (!chatWithGroup?.getGuid()) {
                      toast.error('Group not ready');
                      return;
                    }
                    try {
                      await CometChat.addMembersToGroup(
                        chatWithGroup.getGuid(),
                        [
                          new CometChat.GroupMember(
                            user.getUid(),
                            CometChat.GROUP_MEMBER_SCOPE.ADMIN
                          ),
                        ],
                        []
                      );
                      toast.success(`${user.getName()} added`);
                      setSearchText('');
                      setSearchResults([]);
                      fetchGroupMembers();
                    } catch (err: any) {
                      toast.error(err?.message ?? 'Failed');
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.getAvatar()} />
                  </ListItemAvatar>
                  <ListItemText primary={user.getName()} secondary={user.getUid()} />
                  <AddIcon color='primary' />
                </ListItem>
              ))}
            </Box>
          </DialogContent>
        </StyledDialog>
      )}

      <StyledDialog
        open={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Remove Member?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{removeConfirm?.name}</strong> from this
            conversation?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirm(null)}>Cancel</Button>
          <Button
            variant='contained'
            color='error'
            onClick={async () => {
              if (!removeConfirm || !chatWithGroup) return;
              try {
                await CometChat.kickGroupMember(chatWithGroup.getGuid(), removeConfirm.uid);
                toast.success(`${removeConfirm.name} removed`);
                fetchGroupMembers();
              } catch (err: any) {
                toast.error(err?.message ?? 'Failed to remove');
              } finally {
                setRemoveConfirm(null);
              }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </StyledDialog>
      <StyledDialog
        open={messageInfoOpen}
        onClose={() => setMessageInfoOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          Message Info
          <IconButton
            onClick={() => setMessageInfoOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {receiptTable.length === 0 && (
            <Typography variant='body2'>No recipients for this message.</Typography>
          )}
          {receiptTable.map((user) => (
            <ListItem key={user.uid}>
              <ListItemAvatar>
                <Avatar src={user.avatar} />
              </ListItemAvatar>

              <ListItemText
                primary={user.name}
                secondary={
                  <>
                    <Typography variant='caption' display='block'>
                      Delivered:
                      {user.deliveredAt
                        ? ' ' + new Date(user.deliveredAt * 1000).toLocaleString()
                        : ' —'}
                    </Typography>

                    <Typography variant='caption' display='block'>
                      Read:
                      {user.readAt ? ' ' + new Date(user.readAt * 1000).toLocaleString() : ' —'}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </DialogContent>
      </StyledDialog>
    </>
  );
};
export default ChatboxAdmin;
